import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, User, Crown, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Recipients = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [recipientToDelete, setRecipientToDelete] = useState(null);
  const [newRecipient, setNewRecipient] = useState({
    name: '',
    relationship: '',
    birthday: '',
    birthday_budget: 0,
    interests: '',
    favorite_colors: '',
    clothing_size: '',
    allergies: '',
    notes: ''
  });

  useEffect(() => {
    if (user?.subscription_tier !== 'premium' && user?.subscription_tier !== 'elite') {
      toast.error('Premium or Elite subscription required');
      navigate('/subscription');
      return;
    }
    fetchRecipients();
  }, [user]);

  const fetchRecipients = async () => {
    try {
      const response = await api.get('/recipients');
      setRecipients(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        navigate('/subscription');
      } else {
        toast.error('Failed to load recipients');
      }
    } finally {
      setLoading(false);
    }
  };

  const addRecipient = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...newRecipient,
        interests: newRecipient.interests.split(',').map(s => s.trim()).filter(Boolean),
        favorite_colors: newRecipient.favorite_colors.split(',').map(s => s.trim()).filter(Boolean),
        allergies: newRecipient.allergies.split(',').map(s => s.trim()).filter(Boolean),
      };
      await api.post('/recipients', data);
      toast.success('Recipient added!');
      setShowAddDialog(false);
      setNewRecipient({
        name: '',
        relationship: '',
        birthday: '',
        interests: '',
        favorite_colors: '',
        clothing_size: '',
        allergies: '',
        notes: ''
      });
      fetchRecipients();
    } catch (error) {
      toast.error('Failed to add recipient');
    }
  };

  const openEditDialog = (recipient) => {
    setEditingRecipient(recipient);
    setNewRecipient({
      name: recipient.name,
      relationship: recipient.relationship || '',
      birthday: recipient.birthday ? new Date(recipient.birthday).toISOString().split('T')[0] : '',
      birthday_budget: recipient.birthday_budget || 0,
      interests: recipient.interests.join(', '),
      favorite_colors: recipient.favorite_colors.join(', '),
      clothing_size: recipient.clothing_size || '',
      allergies: recipient.allergies.join(', '),
      notes: recipient.notes || ''
    });
    setShowEditDialog(true);
  };

  const updateRecipient = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...newRecipient,
        interests: newRecipient.interests.split(',').map(s => s.trim()).filter(Boolean),
        favorite_colors: newRecipient.favorite_colors.split(',').map(s => s.trim()).filter(Boolean),
        allergies: newRecipient.allergies.split(',').map(s => s.trim()).filter(Boolean),
      };
      await api.put(`/recipients/${editingRecipient.id}`, data);
      toast.success('Recipient updated!');
      setShowEditDialog(false);
      setEditingRecipient(null);
      setNewRecipient({
        name: '',
        relationship: '',
        birthday: '',
        interests: '',
        favorite_colors: '',
        clothing_size: '',
        allergies: '',
        notes: ''
      });
      fetchRecipients();
    } catch (error) {
      toast.error('Failed to update recipient');
    }
  };

  const deleteRecipient = async () => {
    if (!recipientToDelete) return;
    
    try {
      await api.delete(`/recipients/${recipientToDelete.id}`);
      toast.success('Recipient deleted!');
      setRecipientToDelete(null);
      fetchRecipients();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete recipient');
    }
  };

  const handleDeleteClick = (recipient, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setRecipientToDelete(recipient);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-green-50" data-testid="recipients-page">
      <header className="bg-white shadow-sm border-b border-red-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">
              <span className="gradient-text">👥 Your Recipients</span>
            </h1>
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <Crown className="w-4 h-4" />
              Premium Feature
            </div>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700" data-testid="add-recipient-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Recipient
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="add-recipient-dialog">
              <DialogHeader>
                <DialogTitle>👤 Add New Recipient</DialogTitle>
              </DialogHeader>
              <form onSubmit={addRecipient} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Name *</label>
                    <Input
                      placeholder="Sarah Johnson"
                      value={newRecipient.name}
                      onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                      required
                      data-testid="recipient-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Relationship</label>
                    <Input
                      placeholder="Friend"
                      value={newRecipient.relationship}
                      onChange={(e) => setNewRecipient({ ...newRecipient, relationship: e.target.value })}
                      data-testid="relationship-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Birthday</label>
                  <Input
                    type="date"
                    value={newRecipient.birthday}
                    onChange={(e) => setNewRecipient({ ...newRecipient, birthday: e.target.value })}
                    data-testid="birthday-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Interests (comma-separated)</label>
                  <Input
                    placeholder="Coffee, Reading, Yoga"
                    value={newRecipient.interests}
                    onChange={(e) => setNewRecipient({ ...newRecipient, interests: e.target.value })}
                    data-testid="interests-input"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Favorite Colors</label>
                    <Input
                      placeholder="Blue, Green"
                      value={newRecipient.favorite_colors}
                      onChange={(e) => setNewRecipient({ ...newRecipient, favorite_colors: e.target.value })}
                      data-testid="colors-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Clothing Size</label>
                    <Input
                      placeholder="Medium"
                      value={newRecipient.clothing_size}
                      onChange={(e) => setNewRecipient({ ...newRecipient, clothing_size: e.target.value })}
                      data-testid="size-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Allergies/Dislikes</label>
                  <Input
                    placeholder="Peanuts, Seafood"
                    value={newRecipient.allergies}
                    onChange={(e) => setNewRecipient({ ...newRecipient, allergies: e.target.value })}
                    data-testid="allergies-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Notes</label>
                  <Input
                    placeholder="Additional notes"
                    value={newRecipient.notes}
                    onChange={(e) => setNewRecipient({ ...newRecipient, notes: e.target.value })}
                    data-testid="notes-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="submit-recipient-button">
                  🎁 Add Recipient
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Recipient Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-2xl" data-testid="edit-recipient-dialog">
              <DialogHeader>
                <DialogTitle>✏️ Edit Recipient</DialogTitle>
              </DialogHeader>
              <form onSubmit={updateRecipient} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Name *</label>
                    <Input
                      placeholder="Sarah Johnson"
                      value={newRecipient.name}
                      onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                      required
                      data-testid="edit-recipient-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Relationship</label>
                    <Input
                      placeholder="Friend"
                      value={newRecipient.relationship}
                      onChange={(e) => setNewRecipient({ ...newRecipient, relationship: e.target.value })}
                      data-testid="edit-relationship-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Birthday</label>
                  <Input
                    type="date"
                    value={newRecipient.birthday}
                    onChange={(e) => setNewRecipient({ ...newRecipient, birthday: e.target.value })}
                    data-testid="edit-birthday-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Add to Birthday Tracker</p>
                </div>
                
                {user?.subscription_tier === 'elite' && newRecipient.birthday && (
                  <div>
                    <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                      Birthday Budget
                      <Crown className="w-4 h-4 text-amber-600" />
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={newRecipient.birthday_budget}
                      onChange={(e) => setNewRecipient({ ...newRecipient, birthday_budget: parseFloat(e.target.value) || 0 })}
                      data-testid="edit-birthday-budget-input"
                    />
                    <p className="text-xs text-amber-600 mt-1">Set annual budget for this person&apos;s birthday</p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Interests (comma-separated)</label>
                  <Input
                    placeholder="Coffee, Reading, Yoga"
                    value={newRecipient.interests}
                    onChange={(e) => setNewRecipient({ ...newRecipient, interests: e.target.value })}
                    data-testid="edit-interests-input"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Favorite Colors</label>
                    <Input
                      placeholder="Blue, Green"
                      value={newRecipient.favorite_colors}
                      onChange={(e) => setNewRecipient({ ...newRecipient, favorite_colors: e.target.value })}
                      data-testid="edit-colors-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Clothing Size</label>
                    <Input
                      placeholder="Medium"
                      value={newRecipient.clothing_size}
                      onChange={(e) => setNewRecipient({ ...newRecipient, clothing_size: e.target.value })}
                      data-testid="edit-size-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Allergies/Dislikes</label>
                  <Input
                    placeholder="Peanuts, Seafood"
                    value={newRecipient.allergies}
                    onChange={(e) => setNewRecipient({ ...newRecipient, allergies: e.target.value })}
                    data-testid="edit-allergies-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Notes</label>
                  <Input
                    placeholder="Additional notes"
                    value={newRecipient.notes}
                    onChange={(e) => setNewRecipient({ ...newRecipient, notes: e.target.value })}
                    data-testid="edit-notes-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowEditDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="update-recipient-button">
                    ✏️ Update Recipient
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : recipients.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="p-12 text-center">
              <User className="w-16 h-16 text-red-600 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No recipients yet</h3>
              <p className="text-gray-600 mb-4">Add your first recipient to track preferences and gift history</p>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => setShowAddDialog(true)}
                data-testid="add-first-recipient-button"
              >
                Add Recipient
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recipients.map((recipient) => (
              <Card key={recipient.id} className="glass-card" data-testid={`recipient-card-${recipient.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className="w-6 h-6 text-red-600" />
                      {recipient.name}
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="p-2 hover:bg-blue-100 rounded transition-colors cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openEditDialog(recipient);
                          return false;
                        }}
                        data-testid={`edit-recipient-${recipient.id}`}
                        title="Edit recipient"
                      >
                        <Pencil className="w-4 h-4 text-blue-600 pointer-events-none" />
                      </button>
                      <button
                        type="button"
                        className="p-2 hover:bg-red-100 rounded transition-colors cursor-pointer"
                        onClick={(e) => handleDeleteClick(recipient, e)}
                        data-testid={`delete-recipient-${recipient.id}`}
                        title="Delete recipient"
                      >
                        <Trash2 className="w-4 h-4 text-red-600 pointer-events-none" />
                      </button>
                    </div>
                  </CardTitle>
                  {recipient.relationship && (
                    <p className="text-sm text-gray-600">{recipient.relationship}</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {recipient.birthday && (
                      <p>🎂 {new Date(recipient.birthday).toLocaleDateString()}</p>
                    )}
                    {recipient.interests.length > 0 && (
                      <p>❤️ Loves: {recipient.interests.join(', ')}</p>
                    )}
                    {recipient.clothing_size && (
                      <p>👕 Size: {recipient.clothing_size}</p>
                    )}
                    {recipient.allergies.length > 0 && (
                      <p className="text-red-600">⚠️ {recipient.allergies.join(', ')}</p>
                    )}
                    <div className="pt-2 border-t">
                      <p className="text-gray-600">
                        {recipient.total_gifts_given} gifts given • ${recipient.total_spent.toFixed(2)} total
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!recipientToDelete} onOpenChange={(open) => !open && setRecipientToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recipient?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{recipientToDelete?.name}&quot;? This will not delete associated gifts, but will remove recipient information. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteRecipient}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Recipients;
